import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { iconPropTypes } from "../Icon";
import { separatorPropTypes } from "../Separator";
import { Stepper } from "../Stepper";
import { Text } from "../Text";
import styles from "./ProgressTracker.module.scss";

const DEFAULT_ACTIVE_STEP = {
  background: {
    color: "primary-500",
    type: "circle",
  },
  border: {
    color: "primary-500",
    type: "thick-circle",
  },
  color: "white",
  name: "Airplane2",
};
const DEFAULT_COMPLETED_STEP = {
  border: {
    color: "success-600",
    type: "thin-circle",
  },
  color: "success-600",
  name: "Check",
};
const DEFAULT_INCOMPLETE_STEP = {
  background: {
    color: "neutral-500",
    type: "circle",
  },
  border: {
    color: "neutral-500",
    type: "thin-circle",
  },
  color: "neutral-500",
  name: "Bullet",
};

/**
 * The ProgressTracker component uses the Stepper component to convey progress to a user via
 * a sequence of pre-defined steps, icons and labels.
 */

export const ProgressTracker = (props) => {
  const {
    activeStepIndex = 0,
    className,
    separatorProps = { color: "neutral-500", lineCap: "butt" },
    stepLabels,
    stepStatusIcons = {
      active: DEFAULT_ACTIVE_STEP,
      completed: DEFAULT_COMPLETED_STEP,
      incomplete: DEFAULT_INCOMPLETE_STEP,
    },
    stepTitle,
  } = props;
  const [steps, setSteps] = useState(getSteps());

  useEffect(() => {
    setSteps(getSteps());
  }, [activeStepIndex]);

  return (
    <div className={className}>
      <Stepper {...getStepperProps()} />
      {renderStepCounter()}
    </div>
  );

  function renderStepCounter() {
    const currentStepLabel = stepLabels[activeStepIndex];

    return (
      <div className={styles.stepCounter}>
        <Text className={styles.stepTitle}>{stepTitle}</Text>
        <Text bold>{currentStepLabel}</Text>
      </div>
    );
  }

  function getStepperProps() {
    return {
      separatorProps,
      steps,
    };
  }

  function getSteps() {
    return stepLabels.map((_, index) => {
      const stepStatus = getStepStatus(index);

      return {
        ...stepStatusIcons[stepStatus],
      };
    });
  }

  function getStepStatus(stepIndex) {
    let stepStatus = "incomplete";

    if (stepIndex < activeStepIndex) {
      stepStatus = "completed";
    } else if (stepIndex === activeStepIndex) {
      stepStatus = "active";
    }

    return stepStatus;
  }
};

ProgressTracker.displayName = "ProgressTracker";

export const progressTrackerPropTypes = {
  /** The index of the step that will be set as active */
  activeStepIndex: PropTypes.number,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** The line that will be displayed between each step. Accepts all props of the Separator component */
  separatorProps: PropTypes.shape(separatorPropTypes),

  /** The required array of step labels. This array is also used to determine the number of steps and their statuses */
  stepLabels: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** The icon props for the icon states. Each accepts all props of the Icon component. */
  stepStatusIcons: PropTypes.shape({
    active: PropTypes.shape(iconPropTypes),
    completed: PropTypes.shape(iconPropTypes),
    incomplete: PropTypes.shape(iconPropTypes),
  }),

  /** The required title content that will be displayed above the current step's label */
  stepTitle: PropTypes.string.isRequired,
};

ProgressTracker.propTypes = progressTrackerPropTypes;
